import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { getNameBusinessTheme } from "../../actions/getters";
import {
  startConsentMcpOAuth,
  startGetMcpOAuthClient,
} from "../../actions/mcpOAuth";
import ContainerModalActions from "../../components/Containers/ContainerModalActions";
import Loader from "../../components/Loaders/Loader";
import {
  BUTTON_CANCEL,
  MCP_AUTHORIZE_ACCESS,
  MCP_AUTHORIZE_ADVERTISER_FALLBACK,
  MCP_AUTHORIZE_ADVERTISER_NOT_FOUND,
  MCP_AUTHORIZE_ALLOW,
  MCP_AUTHORIZE_CLIENT_ERROR,
  MCP_AUTHORIZE_HOST_LABEL,
  MCP_AUTHORIZE_PAGE_TITLE,
  MCP_AUTHORIZE_PERMISSION_ERROR,
  MCP_AUTHORIZE_REDIRECT_ERROR,
  MCP_AUTHORIZE_REQUEST_ERROR,
  MCP_AUTHORIZE_TITLE,
  MCP_AUTHORIZE_UNEXPECTED_ERROR,
  MCP_AUTHORIZE_UNKNOWN_HOST,
} from "../../i18n/keysTranslations";
import { ROLES_ALLOWED } from "../../utils/constants";
import { BOX_SHADOW_FIELDS, INPUT_COLOR_BORDER } from "../../utils/colors";
import {
  buildAccessDeniedRedirect,
  clearMcpAuthorizeReturnPath,
  getMcpAuthorizeQueryIssue,
  isRedirectUriRegistered,
  parseRegisteredClient,
  readMcpAuthorizeQuery,
  rememberMcpAuthorizeReturnPath,
} from "../../utils/mcpOAuth";

const messageKeyForKind = (kind) => {
  switch (kind) {
    case "client":
      return MCP_AUTHORIZE_CLIENT_ERROR;
    case "redirect":
      return MCP_AUTHORIZE_REDIRECT_ERROR;
    case "invalid":
      return MCP_AUTHORIZE_REQUEST_ERROR;
    case "permission":
      return MCP_AUTHORIZE_PERMISSION_ERROR;
    case "notFound":
      return MCP_AUTHORIZE_ADVERTISER_NOT_FOUND;
    case "unexpected":
      return MCP_AUTHORIZE_UNEXPECTED_ERROR;
    default:
      return MCP_AUTHORIZE_UNEXPECTED_ERROR;
  }
};

const AuthorizeCard = ({ children }) => (
  <Box
    sx={{
      width: "100%",
      maxWidth: 560,
      display: "flex",
      flexDirection: "column",
      gap: 2.5,
      p: 3,
      borderRadius: 2,
      border: `1px solid ${INPUT_COLOR_BORDER}`,
      backgroundColor: "background.paper",
      boxShadow: BOX_SHADOW_FIELDS,
    }}
  >
    {children}
  </Box>
);

const McpAuthorize = () => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const user = useSelectorApp((state) => state.auth.user);
  const advertiserID = user?.advertiserID || "";
  const businessName = useSelectorApp((state) => state.user.businessName || "");
  const nameBusiness = dispatch(getNameBusinessTheme()) || "";
  const isLoggedIn = Boolean(user) && ROLES_ALLOWED.includes(user?.role);

  const query = useMemo(
    () => readMcpAuthorizeQuery(searchParams),
    [searchParams]
  );
  const queryIssue = getMcpAuthorizeQueryIssue(query);

  const [phase, setPhase] = useState(queryIssue ? "error" : "loading");
  const [errorKind, setErrorKind] = useState(queryIssue);
  const [client, setClient] = useState(null);
  const [consentErrorKind, setConsentErrorKind] = useState(null);

  // Persist the return path before <Navigate> commits. Login must come back
  // to this URL with the query intact, and the value is only the authorize path.
  if (isLoggedIn) {
    clearMcpAuthorizeReturnPath();
  } else {
    rememberMcpAuthorizeReturnPath({
      pathname: location.pathname,
      search: location.search,
    });
  }

  const selectedAdvertiserID = advertiserID;

  useEffect(() => {
    if (!queryIssue) return;
    setPhase("error");
    setErrorKind(queryIssue);
    setClient(null);
  }, [queryIssue]);

  useEffect(() => {
    if (!isLoggedIn || queryIssue) return undefined;

    let cancelled = false;
    const loadClient = async () => {
      setPhase("loading");
      const result = await dispatch(
        startGetMcpOAuthClient({ clientID: query.clientId })
      );
      if (cancelled) return;

      const registered = result?.ok ? parseRegisteredClient(result.data) : null;
      if (
        !registered ||
        !isRedirectUriRegistered({
          redirectUri: query.redirectUri,
          redirectUris: registered?.redirectUris,
        })
      ) {
        setClient(null);
        setPhase("error");
        setErrorKind(result?.ok ? "redirect" : result?.errorKind || "client");
        return;
      }

      setClient(registered);
      setPhase("consent");
      setErrorKind(null);
    };

    loadClient();
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    isLoggedIn,
    query.clientId,
    query.redirectUri,
    query.codeChallenge,
    query.codeChallengeMethod,
    queryIssue,
  ]);

  const selectedAdvertiserName =
    businessName || t(MCP_AUTHORIZE_ADVERTISER_FALLBACK);

  if (!isLoggedIn) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: {
            pathname: location.pathname,
            search: location.search,
          },
        }}
      />
    );
  }

  const onCancel = () => {
    const href = buildAccessDeniedRedirect({
      redirectUri: query.redirectUri,
      state: query.state,
    });
    if (!href) return;
    window.location = href;
  };

  const onAllow = async () => {
    setConsentErrorKind(null);
    const result = await dispatch(
      startConsentMcpOAuth({
        clientID: query.clientId,
        redirectUri: query.redirectUri,
        codeChallenge: query.codeChallenge,
        state: query.state,
        resource: query.resource,
        advertiserID: selectedAdvertiserID,
      })
    );

    if (result?.ok && result.data?.redirectUrl) {
      window.location = result.data.redirectUrl;
      return;
    }

    setConsentErrorKind(result?.errorKind || "unexpected");
  };

  const pageTitle = `${nameBusiness ? `${nameBusiness} - ` : ""}${t(
    MCP_AUTHORIZE_PAGE_TITLE
  )}`;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        px: 2,
        py: { xs: 3, sm: 6 },
      }}
    >
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      {phase === "loading" ? (
        <Loader fullWidth hasMessage message={t(MCP_AUTHORIZE_PAGE_TITLE)} />
      ) : null}

      {phase === "error" ? (
        <AuthorizeCard>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t(MCP_AUTHORIZE_PAGE_TITLE)}
          </Typography>
          <Alert severity="error">{t(messageKeyForKind(errorKind))}</Alert>
        </AuthorizeCard>
      ) : null}

      {phase === "consent" && client ? (
        <AuthorizeCard>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t(MCP_AUTHORIZE_TITLE, {
                clientName: client.clientName || t(MCP_AUTHORIZE_UNKNOWN_HOST),
              })}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 1, color: "text.secondary", lineHeight: 1.5 }}
            >
              {t(MCP_AUTHORIZE_ACCESS, {
                advertiserName: selectedAdvertiserName,
              })}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t(MCP_AUTHORIZE_HOST_LABEL)}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {client.clientName}
            </Typography>
          </Box>

          {consentErrorKind ? (
            <Alert severity="error">
              {t(messageKeyForKind(consentErrorKind))}
            </Alert>
          ) : null}

          <ContainerModalActions
            hasPadding={false}
            propsSecondaryButton={{
              showButton: true,
              text: BUTTON_CANCEL,
              onClick: onCancel,
            }}
            propsPrimaryButton={{
              showButton: true,
              text: MCP_AUTHORIZE_ALLOW,
              disabled: !selectedAdvertiserID,
              onClick: onAllow,
            }}
          />
        </AuthorizeCard>
      ) : null}
    </Box>
  );
};

export default McpAuthorize;
